'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, VStack, Text, Button, Image } from '@skin-seoul/design-system';
import LucideIcon from '@/components/ui/LucideIcon';
interface LearnMoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrls: string[];
}
const SCROLL_EDGE_THRESHOLD_PX = 8;
const LearnMoreModal = ({
  isOpen,
  onClose,
  imageUrls
}: LearnMoreModalProps) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [isAtBottom, setIsAtBottom] = useState(false);
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);
  const handleScroll = useCallback(() => {
    const el = contentRef.current;
    if (!el) {
      return;
    }
    const {
      scrollTop,
      scrollHeight,
      clientHeight
    } = el;
    const atBottom = scrollTop + clientHeight >= scrollHeight - SCROLL_EDGE_THRESHOLD_PX;
    setIsAtBottom(atBottom);
  }, []);
  const handleToggleScroll = useCallback(() => {
    const el = contentRef.current;
    if (!el) {
      return;
    }
    if (isAtBottom) {
      el.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [isAtBottom]);
  if (!isOpen) {
    return null;
  }
  return <Box position="fixed" top={0} left={0} right={0} bottom={0} backgroundColor="black/60" backdropFilter="blur(4px)" zIndex={1000} display="flex" alignItems="center" justifyContent="center" padding={4} data-sentry-element="Box" data-sentry-component="LearnMoreModal" data-sentry-source-file="LearnMoreModal.tsx">
      <Box background="white" borderRadius={12} maxWidth="600px" maxHeight="90vh" width="100%" overflow="hidden" position="relative" data-sentry-element="Box" data-sentry-source-file="LearnMoreModal.tsx">
        {/* Header */}
        <Box position="sticky" top={-5} zIndex={1} background="white" paddingX={6} paddingY={4} borderBottom="1px solid" borderColor="neutral.200" display="flex" alignItems="center" justifyContent="space-between" data-sentry-element="Box" data-sentry-source-file="LearnMoreModal.tsx">
          <Text textStyle="heading.h5" fontWeight="medium" color="neutral.800" data-sentry-element="Text" data-sentry-source-file="LearnMoreModal.tsx">
            Learn More with Visuals
          </Text>
          <Button variant="ghost" size="sm" onClick={onClose} padding={2} borderRadius={8} _hover={{
          backgroundColor: 'neutral.100'
        }} data-sentry-element="Button" data-sentry-source-file="LearnMoreModal.tsx">
            <LucideIcon name="X" size="md" color="neutral.600" data-sentry-element="LucideIcon" data-sentry-source-file="LearnMoreModal.tsx" />
          </Button>
        </Box>

        {/* Content */}
        <Box ref={contentRef} onScroll={handleScroll} padding={6} overflowY="auto" maxHeight="calc(90vh - 120px)" data-sentry-element="Box" data-sentry-source-file="LearnMoreModal.tsx">
          <VStack width="100%" gap={0} data-sentry-element="VStack" data-sentry-source-file="LearnMoreModal.tsx">
            {imageUrls.length === 0 ? <Box display="flex" alignItems="center" justifyContent="center" paddingY={12}>
                <Text color="neutral.500" textStyle="body.medium">
                  No images available
                </Text>
              </Box> : imageUrls.map((imageUrl, index) => <Box key={index} width="100%" overflow="hidden">
                  <Image src={imageUrl} alt={`Learn more image ${index + 1}`} width="100%" height="auto" objectFit="cover" />
                </Box>)}
          </VStack>
        </Box>

        {/* Scroll Toggle FAB */}
        {imageUrls.length > 0 && <Box position="absolute" left="50%" transform="translateX(-50%)" bottom={4} zIndex={10}>
            <Button aria-label={isAtBottom ? 'Scroll to top' : 'Scroll to bottom'} onClick={handleToggleScroll} background="white" borderRadius="100%" width={16} height={16} padding={0} opacity={0.8}>
              <LucideIcon name={isAtBottom ? 'ChevronUp' : 'ChevronDown'} size="xl" color="neutral.400" strokeWidth={1.5} />
            </Button>
          </Box>}
      </Box>
    </Box>;
};
export default LearnMoreModal;